import { Typography } from '@euler/app/components/typography';
import {
  CommonTaskStatusInfo,
  StatusBadgeTheme,
} from '@euler/app/tv/theme/types';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskStatusInfo } from '@euler/model';
import { CommonTaskStatus, InspectionTaskStatus } from '@euler/model/enum';
import clr from 'color';
import { memo } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';

export type StatusBadgeTarget =
  | 'preInspection'
  | 'inspection'
  | 'construction'
  | 'deliveryCheck';

export type StatusBadgeText = {
  preInspection: CommonTaskStatusInfo<string>;
  inspection: CommonTaskStatusInfo<string>;
  construction: CommonTaskStatusInfo<string>;
  deliveryCheck: CommonTaskStatusInfo<string>;
  task: CommonTaskStatusInfo<string> & { canceled: string };
};

const DefaultStatusBadgeTheme: StatusBadgeTheme = {
  preInspection: {
    pending: '#fc7d7b',
    inprogress: '#2e3191',
    finished: '#00AA55',
  },
  inspection: {
    pending: '#fc7d7b',
    inprogress: '#2e3191',
    finished: '#00AA55',
  },
  construction: {
    pending: '#8e78ff',
    inprogress: '#2e3191',
    finished: '#00AA55',
  },
  deliveryCheck: {
    pending: '#06a7c4',
    inprogress: '#4f01bb',
    finished: '#00AA55',
  },
  task: {
    pending: '#939393',
    inprogress: '#4f01bb',
    finished: '#00AA55',
    canceled: '#f6ae05',
  },
};

const DefaultStatusBadgeTextConfig: StatusBadgeText = {
  preInspection: {
    pending: '待预检',
    inprogress: '预检中',
    finished: '已预检',
  },
  inspection: {
    pending: '待检测',
    inprogress: '检测中',
    finished: '已检测',
  },
  construction: {
    pending: '待施工',
    inprogress: '施工中',
    finished: '已施工',
  },
  deliveryCheck: {
    pending: '待质检',
    inprogress: '质检中',
    finished: '已质检',
  },
  task: {
    pending: '已建单',
    inprogress: '进行中',
    finished: '已完工',
    canceled: '已取消',
  },
};

export const StatusBadge = memo(
  ({
    statusInfo,
    style,
    textStyle,
    bordered,
    shade,
    targetStatus,
    isParentTask,
    isRollback,
    theme = DefaultStatusBadgeTheme,
    textConfig = DefaultStatusBadgeTextConfig,
  }: {
    statusInfo: TaskStatusInfo;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    bordered?: boolean;
    shade?: boolean;
    targetStatus?: StatusBadgeTarget;
    isParentTask?: boolean;
    theme?: StatusBadgeTheme;
    textConfig?: StatusBadgeText;
    isRollback?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      status,
      preInspectionStatus,
      inspectionStatus,
      constructionStatus,
      deliveryCheckStatus,
      isConstructionJobScheduled,
    } = statusInfo;

    type StatusInfo = [string, string, string?];
    const statusList: StatusInfo[] = [];

    if (isRollback) {
      statusList.push(['返单中', 'red']);
    } else if (targetStatus != null) {
      switch (targetStatus) {
        case 'preInspection': {
          if (preInspectionStatus === CommonTaskStatus.Pending) {
            statusList.push([
              textConfig.preInspection.pending,
              theme.preInspection.pending,
            ]);
          } else if (preInspectionStatus === CommonTaskStatus.InProgress) {
            statusList.push([
              textConfig.preInspection.inprogress,
              theme.preInspection.inprogress,
            ]);
          } else if (preInspectionStatus === CommonTaskStatus.Finished) {
            statusList.push([
              textConfig.preInspection.finished,
              theme.preInspection.finished,
            ]);
          }
          break;
        }
        case 'inspection': {
          if (inspectionStatus === CommonTaskStatus.Pending) {
            statusList.push([
              textConfig.inspection.pending,
              theme.inspection.pending,
            ]);
          } else if (inspectionStatus === CommonTaskStatus.InProgress) {
            statusList.push([
              textConfig.inspection.inprogress,
              theme.inspection.inprogress,
            ]);
          } else if (inspectionStatus === CommonTaskStatus.Finished) {
            statusList.push([
              textConfig.inspection.finished,
              theme.inspection.finished,
            ]);
          }
          break;
        }
        case 'construction': {
          if (constructionStatus === CommonTaskStatus.Pending) {
            statusList.push([
              textConfig.construction.pending,
              theme.construction.pending,
            ]);
          } else if (constructionStatus === CommonTaskStatus.InProgress) {
            statusList.push([
              textConfig.construction.inprogress,
              theme.construction.inprogress,
            ]);
          } else if (constructionStatus === CommonTaskStatus.Finished) {
            statusList.push([
              textConfig.construction.finished,
              theme.construction.finished,
            ]);
          }
          break;
        }
        case 'deliveryCheck': {
          if (deliveryCheckStatus === CommonTaskStatus.Pending) {
            statusList.push([
              textConfig.deliveryCheck.pending,
              theme.deliveryCheck.pending,
            ]);
          } else if (deliveryCheckStatus === CommonTaskStatus.InProgress) {
            statusList.push([
              textConfig.deliveryCheck.inprogress,
              theme.deliveryCheck.inprogress,
            ]);
          } else if (deliveryCheckStatus === CommonTaskStatus.Finished) {
            statusList.push([
              textConfig.deliveryCheck.finished,
              theme.deliveryCheck.finished,
            ]);
          }
          break;
        }
      }
    } else if (isParentTask && status === InspectionTaskStatus.InProgress) {
      statusList.push([textConfig.task.inprogress, theme.task.inprogress]);
    } else {
      if (status === InspectionTaskStatus.InProgress) {
        if (deliveryCheckStatus === CommonTaskStatus.Finished) {
          statusList.push([
            textConfig.deliveryCheck.finished,
            theme.deliveryCheck.finished,
          ]);
        }
        if (deliveryCheckStatus === CommonTaskStatus.InProgress) {
          statusList.push([
            textConfig.deliveryCheck.inprogress,
            theme.deliveryCheck.inprogress,
          ]);
        } else if (constructionStatus === CommonTaskStatus.Finished) {
          if (deliveryCheckStatus === CommonTaskStatus.Pending) {
            statusList.push([
              textConfig.deliveryCheck.pending,
              theme.deliveryCheck.pending,
            ]);
          }
        } else if (
          constructionStatus === CommonTaskStatus.InProgress ||
          isConstructionJobScheduled
        ) {
          statusList.push([
            textConfig.construction.inprogress,
            theme.construction.inprogress,
          ]);
        } else if (inspectionStatus === CommonTaskStatus.Finished) {
          statusList.push([
            textConfig.construction.pending,
            theme.construction.pending,
          ]);
        } else if (inspectionStatus === CommonTaskStatus.InProgress) {
          statusList.push([
            textConfig.inspection.inprogress,
            theme.inspection.inprogress,
          ]);
        } else if (preInspectionStatus === CommonTaskStatus.InProgress) {
          statusList.push([
            textConfig.preInspection.inprogress,
            theme.preInspection.inprogress,
          ]);
        } else if (preInspectionStatus === CommonTaskStatus.Finished) {
          statusList.push([
            textConfig.inspection.pending,
            theme.inspection.pending,
          ]);
        }
      } else if (status === InspectionTaskStatus.Cancelled) {
        statusList.push([textConfig.task.canceled, theme.task.canceled]);
      } else if (status === InspectionTaskStatus.Finished) {
        statusList.push([textConfig.task.finished, theme.task.finished]);
      }

      if (!statusList.length) {
        statusList.push([textConfig.task.pending, theme.task.pending]);
      }
    }

    const item = statusList[0];

    if (!item) return null;

    const [text, bgColor, color] = item;

    const backgroundColor = bordered
      ? shade
        ? clr(bgColor).alpha(0.15).string()
        : 'transparent'
      : bgColor;

    return (
      <Center
        paddingHorizontal={rpx(8)}
        paddingVertical={0}
        height={rpx(22)}
        borderRadius={rpx(3)}
        backgroundColor={backgroundColor}
        borderWidth={bordered ? 1 : 0}
        borderColor={bordered ? bgColor : 'transparent'}
        style={style}
      >
        <Typography.Text
          light
          size={rpx(12)}
          lineHeight={rpx(22)}
          color={bordered ? bgColor : color ?? '#fff'}
          style={textStyle}
        >
          {text}
        </Typography.Text>
      </Center>
    );
  },
);
